import React, { useState } from "react";
import { Table, Pagination, Form } from "react-bootstrap";
import { PopulationConsumer } from "@models/population.model";
import { PopulationRouteView } from "./PopulationRouteView";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface PopulationListProps {
  launchId: number;
  consumers: PopulationConsumer[];
  total: number;
  page: number;       // 0-based
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export const PopulationList = ({ launchId, consumers, total, page, pageSize, onPageChange, onPageSizeChange }: PopulationListProps) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggle = (consumerId: number) => {
    setExpandedId((prev) => (prev === consumerId ? null : consumerId));
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const start = page * pageSize;
  const hasPrev = page > 0;
  const hasNext = page < pageCount - 1;

  if (total === 0) {
    return (
      <div className="text-center text-muted py-4 border rounded-3 bg-light">
        <p className="mb-0">No people match the current view.</p>
      </div>
    );
  }

  return (
    <>
      <div className="border rounded-3 overflow-hidden">
        <Table hover responsive className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: "3rem" }}>#</th>
              <th>Name</th>
              <th className="text-end" style={{ width: "8rem" }}>Steps Taken</th>
              <th style={{ width: "3rem" }} aria-label="Expand" />
            </tr>
          </thead>
          <tbody>
            {consumers.map((c, i) => {
              const index = start + i;
              const isExpanded = expandedId === c.consumer_id;
              return (
                <React.Fragment key={c.consumer_id}>
                  <tr
                    onClick={() => toggle(c.consumer_id)}
                    style={{ cursor: "pointer" }}
                    className={isExpanded ? "table-active" : undefined}
                  >
                    <td className="text-muted">{index + 1}</td>
                    <td>
                      <span className="fw-semibold">{c.name}</span>
                    </td>
                    <td className="text-end fw-semibold">{c.steps_taken.toLocaleString()}</td>
                    <td className="text-center text-muted">
                      <i className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"}`} />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={4} className="p-0">
                        <PopulationRouteView launchId={launchId} consumerId={c.consumer_id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
        <div className="text-muted small">
          Showing {start + 1}–{start + consumers.length} of {total.toLocaleString()}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            size="sm"
            style={{ width: "auto" }}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n} / page</option>
            ))}
          </Form.Select>
          <Pagination size="sm" className="mb-0">
            <Pagination.Prev disabled={!hasPrev} onClick={() => onPageChange(page - 1)} />
            <Pagination.Item active>{`${page + 1} of ${pageCount}`}</Pagination.Item>
            <Pagination.Next disabled={!hasNext} onClick={() => onPageChange(page + 1)} />
          </Pagination>
        </div>
      </div>
    </>
  );
};
